import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ShoppingCart, Check, Image as ImageIcon, Video as VideoIcon, Download, ArrowLeft, LogOut } from "lucide-react";
import { VideoSelectionDialog } from "../components/VideoSelectionDialog";
import { BrandLogo } from "../components/BrandLogo";
import GalleryThemeToggle from "../components/GalleryThemeToggle";
import ShopFooter from "../components/ShopFooter";
import { toast } from "sonner";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { PublicImagePreview } from "../components/PublicImagePreview";
import { PublicVideoThumbnail } from "../components/PublicVideoThumbnail";
import { ShopLanguageSelector } from "../components/ShopLanguageSelector";
import { ImageSelectionDialog } from "../components/ImageSelectionDialog";
import { DEFAULT_CURRENCY } from "@shared/const";

interface GallerySession {
  projectId: number;
  projectTitle: string;
  cartId: number;
  token: string;
  photographerId: number;
}

export default function Gallery() {
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const { data: systemNameData } = trpc.settings.getPublicSystemName.useQuery(undefined, { staleTime: 60_000 });
  const systemName = systemNameData?.systemName || "Okulus";
  const [session, setSession] = useState<GallerySession | null>(null);
  const utils = trpc.useUtils();
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());
  const [selectedVideos, setSelectedVideos] = useState<Set<number>>(new Set());
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [selectedVideoForDialog, setSelectedVideoForDialog] = useState<{ id: number; filename: string } | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState<Array<{ src: string; alt: string }>>([]);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [selectedImageForDialog, setSelectedImageForDialog] = useState<{ id: number; filename: string } | null>(null);
  const [packageInfo, setPackageInfo] = useState<{
    name: string;
    price: string;
    allowedDigital: number;
    allowedPrint: number;
    allowedVideo: number;
    allowedPrintFormatIds: number[];
  } | null>(null);
  const photographerId = session?.photographerId ?? 0;
  const { data: publicSettings } = trpc.settings.getPublicPayPalSettings.useQuery(
    { photographerId },
    { enabled: photographerId > 0 }
  );
  const currency = publicSettings?.currency || DEFAULT_CURRENCY;
  const formatCurrency = (value: string | number) => {
    const numeric = typeof value === "string" ? parseFloat(value || "0") : value;
    return `${numeric.toFixed(2)} ${currency}`;
  };

  // Auto-logout after 1 hour.
  //
  // Previously these effects depended on `t` from `useTranslation()`, which is
  // reference-unstable across renders: the effect tore down and re-armed the
  // timer on every render, potentially resetting the 1-hour countdown and
  // defeating the expiry. We depend only on `navigate` (stable) and read `t`
  // lazily from a ref at the moment the timer fires.
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  useEffect(() => {
    const sessionData = localStorage.getItem("gallery_session");
    if (!sessionData) {
      navigate("/gallery/login");
      return;
    }
    try {
      const parsed = JSON.parse(sessionData);
      setSession(parsed);
      const loginTime = localStorage.getItem("gallery_login_time");
      if (!loginTime) {
        localStorage.setItem("gallery_login_time", Date.now().toString());
      } else {
        const elapsed = Date.now() - parseInt(loginTime);
        if (elapsed > 3600000) {
          if (parsed.cartId && parsed.token) {
            fetch(`/api/trpc/gallery.clearCart?input=${encodeURIComponent(JSON.stringify({ cartId: parsed.cartId, token: parsed.token }))}`, { method: 'GET' }).catch(() => {});
          }
          localStorage.removeItem("gallery_session");
          localStorage.removeItem("gallery_login_time");
          toast.error(tRef.current.gallery.sessionExpired);
          navigate("/gallery/login");
          return;
        }
      }
    } catch {
      navigate("/gallery/login");
    }
  }, [navigate]);

  useEffect(() => {
    const loginTime = localStorage.getItem("gallery_login_time");
    if (!loginTime) return;

    const elapsed = Date.now() - parseInt(loginTime);
    const remaining = 3600000 - elapsed;
    if (remaining <= 0) return;

    const timer = setTimeout(() => {
      const sessionData = localStorage.getItem("gallery_session");
      if (sessionData) {
        try {
          const parsed = JSON.parse(sessionData);
          if (parsed.cartId && parsed.token) {
            fetch(`/api/trpc/gallery.clearCart?input=${encodeURIComponent(JSON.stringify({ cartId: parsed.cartId, token: parsed.token }))}`, { method: 'GET' }).catch(() => {});
          }
        } catch {}
      }
      localStorage.removeItem("gallery_session");
      localStorage.removeItem("gallery_login_time");
      toast.error(tRef.current.gallery.sessionExpired);
      navigate("/gallery/login");
    }, remaining);

    return () => clearTimeout(timer);
  }, [navigate]);

  // Clear cart on browser close/page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      const sessionData = localStorage.getItem("gallery_session");
      if (sessionData) {
        try {
          const parsed = JSON.parse(sessionData);
          if (parsed.cartId && parsed.token) {
            // Use sendBeacon for reliable logout request
            const clearUrl = `/api/trpc/gallery.clearCart?input=${encodeURIComponent(JSON.stringify({ cartId: parsed.cartId, token: parsed.token }))}`;
            navigator.sendBeacon(clearUrl);
          }
        } catch {}
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const clearCartMutation = trpc.gallery.clearCart.useMutation();

  const handleLogout = async () => {
    if (session?.cartId && session?.token) {
      try {
        await clearCartMutation.mutateAsync({ cartId: session.cartId, token: session.token });
      } catch (e) {
        console.error('[Gallery] Failed to clear cart on logout:', e);
      }
    }
    localStorage.removeItem("gallery_session");
    localStorage.removeItem("gallery_login_time");
    navigate("/gallery/login");
  };

  const { data: images, isLoading: imagesLoading } = trpc.gallery.getImages.useQuery(
    {
      projectId: session?.projectId || 0,
      token: session?.token || "",
    },
    {
      enabled: !!session,
    }
  );

  const { data: videos, isLoading: videosLoading } = trpc.gallery.getVideos.useQuery(
    {
      projectId: session?.projectId || 0,
      token: session?.token || "",
    },
    {
      enabled: !!session,
    }
  );

  const { data: cartData, refetch: refetchCart } = trpc.gallery.getCart.useQuery(
    {
      cartId: session?.cartId || 0,
      token: session?.token || "",
    },
    {
      enabled: !!session,
    }
  );

  // Purchased images (from paid orders for this access code)
  const { data: purchasedData } = trpc.gallery.getPurchasedImageIds.useQuery(
    session ? { token: session.token } : (undefined as any),
    { enabled: !!session }
  );
  const purchasedSet = new Set<number>(purchasedData?.imageIds || []);
  
  // Purchased DIGITAL images (only these can be downloaded)
  const { data: purchasedDigitalData } = trpc.gallery.getPurchasedDigitalImageIds.useQuery(
    session ? { token: session.token } : (undefined as any),
    { enabled: !!session }
  );
  const purchasedDigitalSet = new Set<number>(purchasedDigitalData?.imageIds || []);

  // Purchased videos
  const { data: purchasedVideoData } = trpc.gallery.getPurchasedVideoIds.useQuery(
    session ? { token: session.token } : (undefined as any),
    { enabled: !!session }
  );
  const purchasedVideoSet = new Set<number>(purchasedVideoData?.videoIds || []);

  // Load active packages for the photographer to resolve selected package limits
  const { data: activePackages } = trpc.packages.listActive.useQuery(
    session ? { photographerId: session.photographerId } : (undefined as any),
    { enabled: !!session }
  );

  // Derive limits from selected package in session
  useEffect(() => {
    try {
      const raw = localStorage.getItem("gallery_session");
      const s = raw ? JSON.parse(raw) : null;
      const selectedPackageId: number | null = s?.selectedPackageId ?? null;

      if (!selectedPackageId || !activePackages) {
        setPackageInfo(null);
        return;
      }

      const pkg = activePackages.find((p: any) => p.id === selectedPackageId);
      if (!pkg) {
        setPackageInfo(null);
        return;
      }

      const info = pkg.items.reduce(
        (acc: any, item: any) => {
          if (item.type === "digital") acc.allowedDigital += item.quantity;
          if (item.type === "print") {
            acc.allowedPrint += item.quantity;
            if (item.printFormatId) acc.allowedPrintFormatIds.push(item.printFormatId);
          }
          if (item.type === "video") acc.allowedVideo += item.quantity;
          return acc;
        },
        { name: pkg.name, price: String(pkg.price), allowedDigital: 0, allowedPrint: 0, allowedVideo: 0, allowedPrintFormatIds: [] as number[] }
      );

      setPackageInfo(info);
    } catch {
      setPackageInfo(null);
    }
  }, [activePackages]);

  const addToCartMutation = trpc.gallery.addToCart.useMutation({
    onSuccess: () => {
      console.log('[Gallery] Item added to cart successfully');
      refetchCart();
      toast.success(t.cart.cartUpdated);
    },
    onError: (error: any) => {
      console.error('[Gallery] Failed to add to cart:', error);
      const msg = String(error?.message || '');
      // Map server manual limit error to localized message
      if (msg.includes('Maximale Anzahl digitaler Bilder erreicht') || msg.includes('manual digital limit')) {
        toast.warning(t.settings.maxManualDigitalImagesReached || 'Max. digitale Bilder erreicht');
        return;
      }
      toast.error(error.message || 'Fehler beim Hinzufügen zum Warenkorb');
    },
  });

  const handleToggleImage = (imageId: number, filename: string) => {
    const isAlreadySelected = selectedImages.has(imageId);
    
    if (isAlreadySelected) {
      // Deselect
      const newSelected = new Set(selectedImages);
      newSelected.delete(imageId);
      setSelectedImages(newSelected);
    } else {
      // Show dialog to select product type
      setSelectedImageForDialog({ id: imageId, filename });
      setSelectionDialogOpen(true);
    }
  };

  const handleConfirmImageSelection = (productType: "digital" | "print", printFormat?: string) => {
    if (!session || !selectedImageForDialog) return;

    // Enforce package limits if a package is selected
    if (packageInfo && cartData) {
      const digitalCount = cartData.items.filter((i: any) => i.itemType === "image" && i.productType === "digital").length;
      const printCount = cartData.items.filter((i: any) => i.itemType === "image" && i.productType === "print").length;

      if (productType === "digital") {
        if (digitalCount >= packageInfo.allowedDigital) {
          toast.warning(t.packages.packageLimitWarning);
          return;
        }
      }

      if (productType === "print") {
        if (printCount >= packageInfo.allowedPrint) {
          toast.warning(t.packages.packageLimitWarning);
          return;
        }
        // If package restricts print formats, ensure chosen format is allowed
        if (packageInfo.allowedPrintFormatIds.length > 0) {
          // We only have printFormat name here; skip strict validation if mapping unknown
          // Optional: could be enhanced to map names->ids via printFormats query
        }
      }
    }

    console.log('[Gallery] Adding to cart:', {
      cartId: session.cartId,
      imageId: selectedImageForDialog.id,
      productType,
      printFormat,
    });

    addToCartMutation.mutate({
      cartId: session.cartId,
      imageId: selectedImageForDialog.id,
      itemType: "image" as const,
      productType,
      printFormat,
      token: session.token,
    });

    // Add to selected images set
    const newSelected = new Set(selectedImages);
    newSelected.add(selectedImageForDialog.id);
    setSelectedImages(newSelected);

    // Close dialog
    setSelectionDialogOpen(false);
    setSelectedImageForDialog(null);
  };

  const handleToggleVideo = (videoId: number) => {
    const newSelected = new Set(selectedVideos);
    if (newSelected.has(videoId)) {
      newSelected.delete(videoId);
    } else {
      newSelected.add(videoId);
    }
    setSelectedVideos(newSelected);
  };

  const handleAddToCart = () => {
    if (!session || selectedVideos.size === 0) return;

    const selected = Array.from(selectedVideos);
    if (packageInfo && cartData) {
      const videoCount = cartData.items.filter((i: any) => i.itemType === "video").length;
      const remaining = Math.max(0, packageInfo.allowedVideo - videoCount);
      if (remaining <= 0) {
        toast.warning(t.packages.packageLimitWarning);
        return;
      }
      const toAdd = selected.slice(0, remaining);
      toAdd.forEach((videoId) => {
        addToCartMutation.mutate({
          cartId: session.cartId,
          videoId,
          itemType: "video" as const,
          productType: "digital" as const,
          token: session.token,
        });
      });
      setSelectedVideos(new Set());
      if (selected.length > remaining) {
        toast.message(t.packages.packageLimit, {
          description: t.packages.packageLimitWarning,
        } as any);
      }
      return;
    }

    // No package selected: add all
    selected.forEach((videoId) => {
      addToCartMutation.mutate({
        cartId: session.cartId,
        videoId,
        itemType: "video" as const,
        productType: "digital" as const,
        token: session.token,
      });
    });
    setSelectedVideos(new Set());
  };

  const handleViewCart = () => {
    navigate("/gallery/cart");
  };

  const handleDownloadImage = async (imageId: number, filename: string) => {
    if (!session) return;
    try {
      const data = await utils.client.gallery.getImageOriginalUrl.query({ imageId, token: session.token });
      if (!data?.url) {
        toast.error(t.downloads.downloadError + " " + filename);
        return;
      }
      const blobRes = await fetch(data.url);
      const blob = await blobRes.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(t.downloads.downloadStarted + " " + filename);
    } catch (e: any) {
      toast.error(e?.message || t.downloads.downloadError + " " + filename);
    }
  };

  const handleDownloadVideo = async (videoId: number, filename: string) => {
    if (!session) return;
    try {
      const data = await utils.client.gallery.getVideoOriginalUrl.query({ videoId, token: session.token });
      if (!data?.url) {
        toast.error(t.downloads.downloadError + " " + filename);
        return;
      }
      const blobRes = await fetch(data.url);
      const blob = await blobRes.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(t.downloads.downloadStarted + " " + filename);
    } catch (e: any) {
      toast.error(e?.message || t.downloads.downloadError + " " + filename);
    }
  };

  const handleDownloadAllPurchased = async () => {
    if (!session) return;
    const imagesList = (images || []).filter((img: any) => purchasedSet.has(img.id));
    const videosList = (videos || []).filter((vid: any) => purchasedVideoSet.has(vid.id));
    const total = imagesList.length + videosList.length;
    if (total === 0) return;
    toast.info(`${t.common.download} ${total} ${t.images.imageCount}`);
    for (const img of imagesList) {
      await handleDownloadImage(img.id, img.filename);
      await new Promise(r => setTimeout(r, 400));
    }
    for (const vid of videosList) {
      await handleDownloadVideo(vid.id, vid.filename);
      await new Promise(r => setTimeout(r, 400));
    }
    toast.success(t.downloads.allDownloaded || 'Alle heruntergeladen');
  };

  if (!session) {
    return null;
  }

  if (imagesLoading || videosLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t.common.loading}</p>
      </div>
    );
  }

  const cartItemCount = cartData?.items.length || 0;
  const totalSelected = selectedVideos.size;
  const totalItems = (images?.length || 0) + (videos?.length || 0);

  // Compute remaining quotas & usage (for display)
  let remainingInfo: { digital?: number; print?: number; video?: number } | null = null;
  let usageCounts: { digital?: number; print?: number; video?: number } | null = null;
  if (packageInfo && cartData) {
    const digitalCount = cartData.items.filter((i: any) => i.itemType === "image" && i.productType === "digital").length;
    const printCount = cartData.items.filter((i: any) => i.itemType === "image" && i.productType === "print").length;
    const videoCount = cartData.items.filter((i: any) => i.itemType === "video").length;
    remainingInfo = {
      digital: Math.max(0, packageInfo.allowedDigital - digitalCount),
      print: Math.max(0, packageInfo.allowedPrint - printCount),
      video: Math.max(0, packageInfo.allowedVideo - videoCount),
    };
    usageCounts = { digital: digitalCount, print: printCount, video: videoCount };
  }

  const formatRemainingLabel = (type: "digital" | "print" | "video", value: number) => {
    const templates = {
      digital: t.packages.remainingDigital,
      print: t.packages.remainingPrints,
      video: t.packages.remainingVideos,
    } as const;
    return templates[type]?.replace("{count}", String(value)) ?? String(value);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b sticky top-0 bg-background z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-0">
            <div className="flex items-center gap-4 flex-wrap">
              <Button variant="ghost" size="sm" onClick={() => navigate('/gallery/packages')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> {t.packages?.packageSelection || 'Pakete'}
              </Button>
              <BrandLogo size={40} rounded showName />
              <div>
                <h1 className="text-2xl font-bold">{session.projectTitle}</h1>
                <p className="text-sm text-muted-foreground">
                  {images?.length || 0} {t.images.title} • {videos?.length || 0} Videos
                </p>
              </div>
              {packageInfo && usageCounts && (
                <div className="ml-4 hidden md:flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{t.packages.selectedPackage}: {packageInfo.name}</Badge>
                    <span className="text-xs text-muted-foreground">{formatCurrency(packageInfo.price)}</span>
                  </div>
                  <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                    <span>
                      {t.packages.digital}: {usageCounts.digital}/{packageInfo.allowedDigital} (
                      {formatRemainingLabel("digital", remainingInfo?.digital ?? 0)})
                    </span>
                    <span>
                      {t.packages.print}: {usageCounts.print}/{packageInfo.allowedPrint} (
                      {formatRemainingLabel("print", remainingInfo?.print ?? 0)})
                    </span>
                    <span>
                      {t.packages.video}: {usageCounts.video}/{packageInfo.allowedVideo} (
                      {formatRemainingLabel("video", remainingInfo?.video ?? 0)})
                    </span>
                  </div>
                </div>
              )}
            </div>
            {/* Action buttons: stack on mobile, row on md+ */}
            <div className="flex flex-col gap-2 mt-2 md:mt-0 md:flex-row md:items-center md:gap-4">
              <ShopLanguageSelector />
              <GalleryThemeToggle cartId={session.cartId} token={session.token} />
              {(purchasedSet.size > 0 || purchasedVideoSet.size > 0) && (
                <Button variant="secondary" onClick={handleDownloadAllPurchased}>
                  <Download className="h-4 w-4 mr-2" />
                  {t.downloads.downloadAll || 'Alle herunterladen'}
                </Button>
              )}
              {totalSelected > 0 && (
                <Button onClick={handleAddToCart} disabled={addToCartMutation.isPending}>
                  {addToCartMutation.isPending ? t.common.loading : t.gallery.addToCart}
                  {totalSelected > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {totalSelected}
                    </Badge>
                  )}
                </Button>
              )}
              <Button variant="outline" onClick={handleViewCart}>
                <ShoppingCart className="mr-2 h-4 w-4" />
                {t.gallery.viewCart}
                {cartItemCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {cartItemCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Package Badge */}
      {packageInfo && usageCounts && (
        <div className="md:hidden px-4 mt-3">
          <div className="rounded-md border p-3 text-xs bg-muted/40">
            <div className="flex justify-between items-center mb-2">
              <Badge variant="secondary">{packageInfo.name}</Badge>
              <span className="font-medium">{formatCurrency(packageInfo.price)}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div>
                <span className="font-semibold block">{t.packages.digital}</span>
                <span>
                  {usageCounts.digital}/{packageInfo.allowedDigital} (
                  {formatRemainingLabel("digital", remainingInfo?.digital ?? 0)})
                </span>
              </div>
              <div>
                <span className="font-semibold block">{t.packages.print}</span>
                <span>
                  {usageCounts.print}/{packageInfo.allowedPrint} (
                  {formatRemainingLabel("print", remainingInfo?.print ?? 0)})
                </span>
              </div>
              <div>
                <span className="font-semibold block">{t.packages.video}</span>
                <span>
                  {usageCounts.video}/{packageInfo.allowedVideo} (
                  {formatRemainingLabel("video", remainingInfo?.video ?? 0)})
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      
      <main className="container mx-auto px-4 py-8 flex-1">
        {remainingInfo && (
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-md border p-3 text-sm">
              <span className="font-semibold">{t.packages.digital}:</span>
              <span className="ml-2">{t.packages.remainingDigital.replace("{count}", String(remainingInfo.digital ?? 0))}</span>
            </div>
            <div className="rounded-md border p-3 text-sm">
              <span className="font-semibold">{t.packages.print}:</span>
              <span className="ml-2">{t.packages.remainingPrints.replace("{count}", String(remainingInfo.print ?? 0))}</span>
            </div>
            <div className="rounded-md border p-3 text-sm">
              <span className="font-semibold">{t.packages.video}:</span>
              <span className="ml-2">{t.packages.remainingVideos.replace("{count}", String(remainingInfo.video ?? 0))}</span>
            </div>
          </div>
        )}
        {totalItems === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <ImageIcon className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t.gallery.emptyGallery}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Videos Section */}
            {videos && videos.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <VideoIcon className="h-5 w-5" />
                  Videos ({videos.length})
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {videos.map((video: any) => {
                    const isSelected = selectedVideos.has(video.id);
                    const isInCart = cartData?.items.some((item: any) => item.itemType === "video" && item.videoId === video.id);
                    const isPurchased = purchasedVideoSet.has(video.id);
                    const fileSizeMB = (video.fileSize / (1024 * 1024)).toFixed(2);

                    return (
                      <Card
                        key={video.id}
                        className={`cursor-pointer transition-all ${
                          isSelected ? "ring-4 ring-primary" : "hover:ring-2 hover:ring-primary/50"
                        }`}
                        onClick={() => { setSelectedVideoForDialog({ id: video.id, filename: video.filename }); setVideoDialogOpen(true); }}
                      >
                        <CardContent className="p-4">
                          <div className="aspect-video bg-muted rounded-lg flex items-center justify-center mb-3 relative overflow-hidden">
                            <PublicVideoThumbnail
                              videoId={video.id}
                              token={session!.token}
                              alt={video.filename}
                              className="w-full h-full object-cover"
                            />
                            
                            {/* Selection indicator */}
                            {isSelected && (
                              <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                                <Check className="h-5 w-5" />
                              </div>
                            )}

                            {/* In cart indicator */}
                            {isInCart && !isSelected && (
                              <div className="absolute top-2 right-2 bg-green-600 text-white rounded-full p-1">
                                <ShoppingCart className="h-5 w-5" />
                              </div>
                            )}

                            {/* Purchased download button */}
                            {isPurchased && (
                              <div className="absolute bottom-2 right-2">
                                <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleDownloadVideo(video.id, video.filename); }}>
                                  <Download className="h-4 w-4 mr-1" />
                                  {t.downloads.download}
                                </Button>
                              </div>
                            )}
                          </div>
                          <p className="font-medium text-sm truncate">{video.filename}</p>
                          <p className="text-xs text-muted-foreground">{fileSizeMB} MB</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Images Section */}
            {images && images.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  {t.images.title} ({images.length})
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {images.map((image: any) => {
              const isInCart = cartData?.items.some((item: any) => item.itemType === "image" && item.imageId === image.id);
              const isPurchased = purchasedSet.has(image.id);
              const isDigitalPurchased = purchasedDigitalSet.has(image.id);

              return (
                <div
                  key={image.id}
                  className="relative aspect-square bg-muted rounded-lg overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-primary/50"
                  onClick={() => handleToggleImage(image.id, image.filename)}
                  onDoubleClick={async (e) => {
                    e.stopPropagation();
                    const index = images?.findIndex((img: any) => img.id === image.id) || 0;
                    setLightboxIndex(index);
                    
                    // Load signed URLs for all images
                    if (images) {
                      const slides = await Promise.all(
                        images.map(async (img: any) => {
                          try {
                            const response = await fetch(
                              `/api/trpc/gallery.getPreviewUrl?input=${encodeURIComponent(
                                JSON.stringify({ imageId: img.id, token: session.token })
                              )}`
                            );
                            const data = await response.json();
                            return {
                              src: data.result.data.url || `https://via.placeholder.com/1200x1200?text=${encodeURIComponent(img.filename)}`,
                              alt: img.filename,
                            };
                          } catch (error) {
                            return {
                              src: `https://via.placeholder.com/1200x1200?text=${encodeURIComponent(img.filename)}`,
                              alt: img.filename,
                            };
                          }
                        })
                      );
                      setLightboxImages(slides);
                    }
                    
                    setLightboxOpen(true);
                  }}
                >
                  {/* Image preview */}
                  <PublicImagePreview
                    imageId={image.id}
                    token={session.token}
                    alt={image.filename}
                    className="w-full h-full"
                  />

                  {/* In cart indicator */}
                  {isInCart && (
                    <div className="absolute top-2 right-2 bg-green-600 text-white rounded-full p-1">
                      <ShoppingCart className="h-5 w-5" />
                    </div>
                  )}

                  {/* Purchased download button - only for digital purchases */}
                  {isDigitalPurchased && (
                    <div className="absolute bottom-2 right-2">
                      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleDownloadImage(image.id, image.filename); }}>
                        <Download className="h-4 w-4 mr-1" />
                        {t.downloads.download}
                      </Button>
                    </div>
                  )}

                  {/* Watermark overlay disabled; real watermark comes from processed preview */}
                </div>
              );
            })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Selection info */}
        {totalSelected > 0 && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-6 py-3 rounded-full shadow-lg">
            <p className="font-medium">
              {totalSelected} item{totalSelected === 1 ? '' : 's'} selected
            </p>
          </div>
        )}
      </main>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => {
          setLightboxOpen(false);
          // Clear lightbox images when closing
          setLightboxImages([]);
        }}
        index={lightboxIndex}
        slides={lightboxImages}
      />

      {/* Image Selection Dialog */}
      <ImageSelectionDialog
        open={selectionDialogOpen}
        onClose={() => {
          setSelectionDialogOpen(false);
          setSelectedImageForDialog(null);
        }}
        onConfirm={handleConfirmImageSelection}
        imageFilename={selectedImageForDialog?.filename || ""}
        photographerId={session?.photographerId || 0}
        allowedPrintFormatIds={packageInfo?.allowedPrintFormatIds}
        currency={currency}
      />

      {/* Video Selection Dialog */}
      {selectedVideoForDialog && (
        <VideoSelectionDialog
          open={videoDialogOpen}
          onClose={() => { setVideoDialogOpen(false); setSelectedVideoForDialog(null); }}
          videoId={selectedVideoForDialog.id}
          filename={selectedVideoForDialog.filename}
          token={session.token}
          cartId={session.cartId}
          onAdded={() => { refetchCart(); }}
          addToCart={(videoId: number) => {
            addToCartMutation.mutate({
              cartId: session.cartId,
              videoId,
              itemType: "video" as const,
              productType: "digital" as const,
              token: session.token,
            });
          }}
        />
      )}

      {/* Logout Button - Fixed Bottom Right */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={handleLogout}
          className="gap-2 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-700 transition-colors shadow-lg"
        >
          <LogOut className="h-4 w-4" />
          <span>{t.gallery.logout}</span>
        </Button>
      </div>

      <ShopFooter />
    </div>
  );
}
